import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton
} from '@mui/material';
import MainLayout from '../../layout/MainLayout';
import SearchIcon from '@mui/icons-material/Search';
import FolderIcon from '@mui/icons-material/Folder';
import StarIcon from '@mui/icons-material/Star';
import GroupIcon from '@mui/icons-material/Group';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import NotificationsIcon from '@mui/icons-material/Notifications';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Link, useNavigate } from 'react-router-dom';

// Import JSON data
import parametersData from '../../data/parameters.json';
import parameterListsData from '../../data/parameterLists.json';
import cohortsData from '../../data/cohorts.json';
import recentActivityData from '../../data/recentActivity.json';
import notificationsData from '../../data/notifications.json';

// Helper function to format time ago
const getTimeAgo = (timestamp) => {
  const now = new Date();
  const activityTime = new Date(timestamp);
  const diffMs = now - activityTime;
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffHours < 1) return 'vor wenigen Minuten';
  if (diffHours < 24) return `vor ${diffHours} Stunden`;
  if (diffDays === 1) return 'vor 1 Tag';
  return `vor ${diffDays} Tagen`;
};

const OlympDashboard = () => {
  const navigate = useNavigate();

  // Load data from JSON files and localStorage
  const [favorites, setFavorites] = useState([]);
  const [myLists, setMyLists] = useState([]);

  // Load favorites from localStorage
  useEffect(() => {
    const storedFavorites = JSON.parse(localStorage.getItem('olymp_favorites') || '[]');
    const favoriteParams = parametersData.parameters.filter(p =>
      storedFavorites.includes(p.id)
    );
    setFavorites(favoriteParams);

    // Load user's lists (filter by current user or public)
    const userLists = parameterListsData.lists.filter(list =>
      list.createdBy === 'Max Mustermann' || list.isPublic
    );
    setMyLists(userLists.slice(0, 3)); // Show only first 3
  }, []);

  // Calculate stats from real data
  const stats = {
    totalParameters: parametersData.parameters.length,
    omopMapped: parametersData.parameters.filter(p =>
      p.omopMapping?.status === 'validated' || p.omopMapping?.status === 'mapped'
    ).length,
    pendingMappings: parametersData.parameters.filter(p =>
      p.omopMapping?.status === 'pending' || !p.omopMapping
    ).length,
    favoritesCount: favorites.length,
    myLists: parameterListsData.lists.length,
    myCohorts: cohortsData.cohorts.length,
    myRequests: cohortsData.cohorts.length // Anzahl der Anfragen basierend auf Kohorten
  };

  // Get recent activity from JSON
  const recentActivity = recentActivityData.activities.slice(0, 4).map(activity => ({
    ...activity,
    time: getTimeAgo(activity.timestamp)
  }));

  // Get notifications from JSON
  const notifications = notificationsData.notifications.slice(0, 3);

  return (
    <MainLayout>
      <Box sx={{ flexGrow: 1 }}>
        {/* Welcome Section */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            background: theme => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            mb: 3
          }}
        >
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Willkommen im ARGUS Olymp Data Catalog
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
            Entdecken, Analysieren und Nutzen Sie Ihre klinischen Forschungsdaten
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<SearchIcon />}
              onClick={() => navigate('/catalog')}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5
              }}
            >
              Datenkatalog durchsuchen
            </Button>
            <Button
              variant="outlined"
              startIcon={<GroupIcon />}
              onClick={() => navigate('/cohorts')}
              sx={{
                borderColor: 'white',
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' }
              }}
            >
              Kohorten verwalten
            </Button>
            <Button
              variant="outlined"
              startIcon={<AssignmentIcon />}
              onClick={() => navigate('/requests')}
              sx={{
                borderColor: 'white',
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' }
              }}
            >
              Datenanfragen
            </Button>
          </Box>
        </Paper>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: '100%',
                transition: 'transform 0.3s, box-shadow 0.3s',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                }
              }}
              onClick={() => navigate('/catalog')}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      Parameter im Katalog
                    </Typography>
                    <Typography variant="h4" sx={{ mt: 1 }}>
                      {stats.totalParameters}
                    </Typography>
                  </Box>
                  <FolderIcon sx={{ fontSize: 50, color: 'primary.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: '100%',
                transition: 'transform 0.3s, box-shadow 0.3s',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                }
              }}
              onClick={() => navigate('/catalog')}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      OMOP gemappt
                    </Typography>
                    <Typography variant="h4" sx={{ mt: 1 }}>
                      {stats.omopMapped}
                    </Typography>
                    <Typography variant="caption" color="success.main" fontWeight="600">
                      {Math.round((stats.omopMapped / stats.totalParameters) * 100)}% Coverage
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 50, color: 'success.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: '100%',
                transition: 'transform 0.3s, box-shadow 0.3s',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                }
              }}
              onClick={() => navigate('/catalog')}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      Meine Favoriten
                    </Typography>
                    <Typography variant="h4" sx={{ mt: 1 }}>
                      {stats.favoritesCount}
                    </Typography>
                  </Box>
                  <StarIcon sx={{ fontSize: 50, color: 'warning.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: '100%',
                transition: 'transform 0.3s, box-shadow 0.3s',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                }
              }}
              onClick={() => navigate('/requests')}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      Datenanfragen
                    </Typography>
                    <Typography variant="h4" sx={{ mt: 1 }}>
                      {stats.myRequests}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stats.myCohorts} Kohorten
                    </Typography>
                  </Box>
                  <AssignmentIcon sx={{ fontSize: 50, color: 'info.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Content Grid */}
        <Grid container spacing={3}>
          {/* Favorites Widget */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    <StarIcon sx={{ verticalAlign: 'middle', mr: 1, color: 'warning.main' }} />
                    Meine Favoriten
                  </Typography>
                  {favorites.length > 0 && (
                    <Button
                      size="small"
                      onClick={() => navigate('/catalog')}
                    >
                      Alle anzeigen
                    </Button>
                  )}
                </Box>
                {favorites.length > 0 ? (
                  <List dense>
                    {favorites.slice(0, 5).map(fav => (
                      <ListItem
                        key={fav.id}
                        button
                        onClick={() => navigate(`/parameter/${fav.id}`)}
                        secondaryAction={
                          <Chip
                            label={fav.omopMapping?.status === 'validated' || fav.omopMapping?.status === 'mapped' ? 'OMOP' : 'Pending'}
                            size="small"
                            color={fav.omopMapping?.status === 'validated' || fav.omopMapping?.status === 'mapped' ? 'success' : 'default'}
                          />
                        }
                      >
                        <ListItemIcon>
                          <StarIcon color="warning" />
                        </ListItemIcon>
                        <ListItemText
                          primary={fav.name}
                          secondary={fav.category}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <StarIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Noch keine Favoriten
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                      Markieren Sie Parameter im Katalog als Favoriten
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate('/catalog')}
                    >
                      Zum Katalog
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Recent Activity */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Letzte Aktivitäten
                </Typography>
                <List dense>
                  {recentActivity.map(activity => (
                    <ListItem key={activity.id}>
                      <ListItemText
                        primary={activity.text}
                        secondary={activity.time}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* My Lists */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    Meine Parameterlisten
                  </Typography>
                  <IconButton size="small" color="primary">
                    <AddIcon />
                  </IconButton>
                </Box>
                <List dense>
                  {myLists.map(list => (
                    <ListItem
                      key={list.id}
                      button
                      onClick={() => navigate('/catalog')}
                      secondaryAction={
                        <Typography variant="body2" color="textSecondary">
                          {list.parameterIds.length} Parameter
                        </Typography>
                      }
                    >
                      <ListItemIcon>
                        <FolderIcon color={list.isPublic ? 'primary' : 'action'} />
                      </ListItemIcon>
                      <ListItemText
                        primary={list.name}
                        secondary={list.isPublic ? 'Öffentlich' : 'Privat'}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Notifications */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  <NotificationsIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Benachrichtigungen
                  {notifications.filter(n => n.unread).length > 0 && (
                    <Chip
                      label={notifications.filter(n => n.unread).length}
                      size="small"
                      color="error"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>
                <List dense>
                  {notifications.map(notif => (
                    <ListItem
                      key={notif.id}
                      sx={{
                        backgroundColor: notif.unread ? 'action.hover' : 'transparent',
                        borderRadius: 1,
                        mb: 0.5
                      }}
                    >
                      <ListItemText
                        primary={notif.text}
                        primaryTypographyProps={{
                          fontWeight: notif.unread ? 600 : 400
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </MainLayout>
  );
};

export default OlympDashboard;
